import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FC, useMemo } from "react";
import TableButtonComponent from "../../../../../common/components/table/button/table-button.component";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerHelper from "../../../common/order-optimizer.helper";
import OrderOptimizerTaxiDetailsOrdersTableRow from "../common/types/order-optimizer-taxi-details-orders-table-row";
import orderOptimizerTaxiDetailsOrdersTableHelper from "./order-optimizer-taxi-details-orders-table.helper";
import OrderOptimizerDetailsTableDotComponent from "../../../common/dot/order-optimizer-details-orders-table-dot.component";
import { OrderOptimizerTaxiDetailsItemOrders } from "../common/types/order-optimizer-taxi-details-item";
import orderOptimizerTaxiDetailsHelper from "../order-optimizer-taxi-details.helper";

type OrderOptimizerTaxiDetailsOrdersTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOrderItems?: OrderOptimizerTaxiDetailsItemOrders[];
  hiddenOrders?: number[];
  onOrderVisibilityClick: (id: number) => void;
};

const OrderOptimizerTaxiDetailsOrdersTableComponent: FC<
  OrderOptimizerTaxiDetailsOrdersTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerTaxiDetailsOrdersTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createTableRow = (
    detailsOrderItems: OrderOptimizerTaxiDetailsItemOrders,
    index: number,
    hiddenOrders: number[],
    humanIds: number[],
  ): OrderOptimizerTaxiDetailsOrdersTableRow => {
    const distanceLabel = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance,
    );
    const distanceTitle = orderOptimizerTaxiDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance,
    );

    const totalTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime,
    );
    const totalTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime,
    );

    const haltingTimeLabel = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerTaxiDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime,
    );

    const isButtonVisible =
      props.detailsOrderItems!.length - props.hiddenOrders!.length >= 3 ||
      hiddenOrders.includes(humanIds[index]);
    const isOrderHidden = hiddenOrders.includes(humanIds[index]);
    const dotColor = isOrderHidden
      ? "#808080"
      : orderOptimizerHelper.getOrderColor(index);

    return {
      id: uuidService.generate(),
      className: isOrderHidden
        ? "order_optimizer_taxi_details_orders_table__hidden_row"
        : undefined,
      value: {
        humanId: (
          <div className="order_optimizer_taxi_details_orders_table__humanId">
            <OrderOptimizerDetailsTableDotComponent color={dotColor} />
            <span title={String(detailsOrderItems.humanId)}>
              {detailsOrderItems.humanId}
            </span>
          </div>
        ),
        cargoCompany: (
          <div title={detailsOrderItems.cargoCompany}>
            {detailsOrderItems.cargoCompany}
          </div>
        ),
        billingModel: (
          <div title={detailsOrderItems.billingModel}>
            {detailsOrderItems.billingModel}
          </div>
        ),
        taxiCorporation: (
          <div title={detailsOrderItems.taxiCorporation}>
            {detailsOrderItems.taxiCorporation}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={detailsOrderItems.haltingTime < 0 ? "time_delayed" : ""}
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        action: (
          <TableButtonComponent
            icon={faEye}
            type={"brand"}
            onClick={() =>
              props.onOrderVisibilityClick(detailsOrderItems.humanId)
            }
            isDisabled={!isButtonVisible}
          />
        ),
      },
    };
  };

  const rows: OrderOptimizerTaxiDetailsOrdersTableRow[] = useMemo(() => {
    const detailsOrderItems = props.detailsOrderItems || [];
    const humanIds = detailsOrderItems.map((item) => item.humanId);

    return detailsOrderItems.map((item, index) =>
      createTableRow(item, index, props.hiddenOrders ?? [], humanIds),
    );
  }, [props.detailsOrderItems, props.hiddenOrders, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerTaxiDetailsOrdersTableComponent;
